import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LogOut, Bookmark, Download, User as UserIcon, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useMember } from "@/lib/member";

export default function MyAccount() {
  const { member, loading, signOut } = useMember();
  const [activity, setActivity] = useState({ bookmarks: [], downloads: [] });
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (member) {
      api.get("/members/activity").then((r) => setActivity(r.data)).finally(() => setBusy(false));
    }
  }, [member]);

  if (loading) {
    return (
      <main className="min-h-[60vh] flex items-center justify-center text-[#1F2547]">
        Loading…
      </main>
    );
  }
  if (!member) return <Navigate to="/" replace />;

  return (
    <main data-testid="my-account-page" className="min-h-[60vh]">
      <section className="bg-[#EFE9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 md:py-20">
          <div className="flex flex-col md:flex-row md:items-center gap-6">
            {member.picture ? (
              <img
                src={member.picture}
                alt=""
                referrerPolicy="no-referrer"
                className="w-20 h-20 rounded-full ring-4 ring-white brutal-shadow-sm object-cover"
              />
            ) : (
              <div className="w-20 h-20 rounded-full ring-4 ring-white brutal-shadow-sm bg-[#9F87D8] text-white flex items-center justify-center">
                <UserIcon className="w-8 h-8" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#9F87D8]">
                / My Percept
              </span>
              <h1 className="font-display text-3xl md:text-5xl font-black tracking-tight text-[#1F2547] mt-1">
                Hi {member.name?.split(" ")[0] || "there"} 👋
              </h1>
              <p className="text-sm text-[#5B6079] font-medium mt-1">
                {member.email} · <b className="uppercase text-[#9F87D8]">{member.tier || "free"}</b> member
              </p>
            </div>
            <button
              data-testid="my-account-signout"
              onClick={signOut}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-[#1F2547] font-bold rounded-full brutal-shadow-sm btn-press"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="grid md:grid-cols-3 gap-5 mb-10">
          <div className="rounded-2xl bg-white brutal-shadow-sm p-5">
            <div className="w-10 h-10 rounded-xl bg-[#D4EDDB] flex items-center justify-center mb-3">
              <Bookmark className="w-5 h-5 text-[#1F2547]" />
            </div>
            <div className="font-display text-3xl font-black text-[#1F2547]">
              {activity.bookmarks.length}
            </div>
            <div className="text-sm text-[#5B6079] font-medium">Bookmarks</div>
          </div>
          <div className="rounded-2xl bg-white brutal-shadow-sm p-5">
            <div className="w-10 h-10 rounded-xl bg-[#C7DCF7] flex items-center justify-center mb-3">
              <Download className="w-5 h-5 text-[#1F2547]" />
            </div>
            <div className="font-display text-3xl font-black text-[#1F2547]">
              {activity.downloads.length}
            </div>
            <div className="text-sm text-[#5B6079] font-medium">Worksheets downloaded</div>
          </div>
          <div className="rounded-2xl bg-[#1F2547] text-white brutal-shadow-sm p-5">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center mb-3">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="font-display text-lg font-black">Upgrade coming soon</div>
            <div className="text-xs text-white/70 font-medium mt-1">
              Premium tier — priority replies, exclusive content
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          <div>
            <h2 className="font-display text-xl font-black text-[#1F2547] mb-4">
              Recent bookmarks
            </h2>
            {busy ? (
              <div className="text-sm text-[#5B6079]">Loading…</div>
            ) : activity.bookmarks.length === 0 ? (
              <div className="rounded-2xl bg-white brutal-shadow-sm p-5 text-sm text-[#5B6079] font-medium">
                Bookmark any blog, video, or worksheet — it&apos;ll show up here.
              </div>
            ) : (
              <div className="space-y-2">
                {activity.bookmarks.slice(0, 10).map((b) => (
                  <div key={b.id} className="rounded-xl bg-white brutal-shadow-sm p-3 text-sm text-[#1F2547] font-medium flex items-center gap-3">
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-[#D4EDDB]">
                      {b.item_type}
                    </span>
                    <span className="flex-1 truncate">{b.item_id}</span>
                    <span className="text-xs text-[#5B6079]">
                      {new Date(b.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <h2 className="font-display text-xl font-black text-[#1F2547] mb-4">
              Recent downloads
            </h2>
            {busy ? (
              <div className="text-sm text-[#5B6079]">Loading…</div>
            ) : activity.downloads.length === 0 ? (
              <div className="rounded-2xl bg-white brutal-shadow-sm p-5 text-sm text-[#5B6079] font-medium">
                <Link to="/worksheets" className="text-[#9F87D8] font-bold hover:underline">
                  Browse worksheets
                </Link>{" "}
                to grab your first PDF.
              </div>
            ) : (
              <div className="space-y-2">
                {activity.downloads.slice(0, 10).map((d) => (
                  <div key={d.id} className="rounded-xl bg-white brutal-shadow-sm p-3 text-sm text-[#1F2547] font-medium flex items-center gap-3">
                    <Download className="w-4 h-4 text-[#9F87D8]" />
                    <span className="flex-1 truncate">Worksheet · {d.worksheet_id}</span>
                    <span className="text-xs text-[#5B6079]">
                      {new Date(d.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}
